import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
    Box, Typography, Paper, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, MenuItem, Select, FormControl,
    InputLabel, Grid, Card, Stack, TextField, Chip, CircularProgress, Divider
} from "@mui/material";
import { collection, getDocs, doc, setDoc, query } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { format, differenceInMinutes, isAfter, setHours, setMinutes } from "date-fns";

function Staffattendance() {
    const legendItems = [
        { label: "Present", color: "#e8f5e9", border: "#a5d6a7" },
        { label: "Late Mark", color: "#fff3e0", border: "#ffcc80" },
        { label: "Half Day", color: "#e3f2fd", border: "#bbdefb" },
        { label: "Absent", color: "#ffebee", border: "#ef9a9a" },
        { label: "Holiday", color: "#f5f5f5", border: "#e0e0e0" },
    ];

    const [staffList, setStaffList] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [attendanceData, setAttendanceData] = useState({});
    const [viewDate, setViewDate] = useState(new Date());
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Form States
    const [selectedDate, setSelectedDate] = useState(null);
    const [status, setStatus] = useState("Present");
    const [checkInTime, setCheckInTime] = useState("09:00");
    const [checkOutTime, setCheckOutTime] = useState("18:00");
    const [checkInLocation, setCheckInLocation] = useState("");
    const [checkOutLocation, setCheckOutLocation] = useState("");

    const [checkInImg, setCheckInImg] = useState("");
    const [checkInFile, setCheckInFile] = useState(null);
    const [checkOutImg, setCheckOutImg] = useState("");
    const [checkOutFile, setCheckOutFile] = useState(null);

    const [counts, setCounts] = useState({ present: 0, absent: 0, halfDay: 0, late: 0 });

    useEffect(() => {
        loadStaff();
        loadAttendance();
    }, []);

    useEffect(() => {
        if (selectedStaff) calculateMonthlyStats(viewDate);
    }, [attendanceData, selectedStaff, viewDate]);

    const loadStaff = async () => {
        try {
            const q = query(collection(db, "authroles"));
            const snap = await getDocs(q);
            const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            list.sort((a, b) => {
                const nameA = (a.name || a.staffName || a.email || "").toLowerCase();
                const nameB = (b.name || b.staffName || b.email || "").toLowerCase();
                return nameA.localeCompare(nameB);
            });

            setStaffList(list);
        } catch (e) {
            console.error("Error loading staff:", e);
        }
    };

    const loadAttendance = async () => {
        try {
            const snapshot = await getDocs(collection(db, "staffattendance"));
            let map = {};
            snapshot.docs.forEach(d => {
                const data = d.data();
                const staffKey = data.staffId || data.uid;
                if (staffKey && data.date) {
                    const key = `${staffKey}_${data.date}`;
                    map[key] = { docId: d.id, ...data };
                }
            });
            setAttendanceData(map);
        } catch (e) {
            console.error("Error loading attendance data:", e);
        }
    };

    const handleDateClick = (date) => {
        if (!selectedStaff) return;
        const dStr = format(date, "yyyy-MM-dd");
        const existing = attendanceData[`${selectedStaff.id}_${dStr}`];

        setSelectedDate(date);
        setStatus(existing?.status || "Present");
        setCheckInImg(existing?.checkInImage || "");
        setCheckOutImg(existing?.checkOutImage || "");
        setCheckInLocation(existing?.checkInLocation || existing?.location || "");
        setCheckOutLocation(existing?.checkOutLocation || "");

        // Extract time strings (HH:mm) safely from ISO formats or fallback defaults
        setCheckInTime(existing?.checkInTime ? format(new Date(existing.checkInTime), "HH:mm") : "09:00");
        setCheckOutTime(existing?.checkOutTime ? format(new Date(existing.checkOutTime), "HH:mm") : "18:00");
        setCheckInFile(null);
        setCheckOutFile(null);
        setOpen(true);
    };

    const handleSave = async () => {
        if (!selectedDate || !selectedStaff) return;
        setLoading(true);

        try {
            const dStr = format(selectedDate, "yyyy-MM-dd");
            const docId = `${selectedStaff.id}_${dStr}`;

            // Helper to assemble real ISO dates out of time strings
            const getFullISO = (timeStr) => {
                if (!timeStr) return null;
                const [h, m] = timeStr.split(':');
                const d = new Date(selectedDate);
                d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
                return d.toISOString();
            };

            const isoIn = status !== "Absent" ? getFullISO(checkInTime) : null;
            const isoOut = status !== "Absent" ? getFullISO(checkOutTime) : null;

            let finalStatus = status;

            // Recalculate parameters based on operational attendance logic rules
            if (status === "Present" || status === "Late Mark" || status === "Half Day") {
                if (isoIn) {
                    const checkInDate = new Date(isoIn);
                    const lateLimit = setMinutes(setHours(new Date(selectedDate), 9), 30); // 09:30 AM cutoff

                    if (isoOut) {
                        const checkOutDate = new Date(isoOut);
                        const workMins = differenceInMinutes(checkOutDate, checkInDate);

                        if (workMins > 0 && workMins < 240) {
                            finalStatus = "Half Day";
                        } else if (isAfter(checkInDate, lateLimit)) {
                            finalStatus = "Late Mark";
                        } else {
                            finalStatus = "Present";
                        }
                    } else {
                        finalStatus = isAfter(checkInDate, lateLimit) ? "Late Mark" : "Present";
                    }
                }
            }

            // Optional Image uploads matching manual overwrites
            let finalInUrl = checkInImg, finalOutUrl = checkOutImg;
            if (checkInFile) {
                const refIn = ref(storage, `attendance/${docId}_in.jpg`);
                await uploadBytes(refIn, checkInFile);
                finalInUrl = await getDownloadURL(refIn);
            }
            if (checkOutFile) {
                const refOut = ref(storage, `attendance/${docId}_out.jpg`);
                await uploadBytes(refOut, checkOutFile);
                finalOutUrl = await getDownloadURL(refOut);
            }

            // Sync updated fields to Firestore doc
            await setDoc(doc(db, "staffattendance", docId), {
                uid: selectedStaff.id,
                staffId: selectedStaff.id,
                staffName: selectedStaff.name || selectedStaff.staffName || "Staff Member",
                role: selectedStaff.role || "Staff",
                date: dStr,
                status: finalStatus,
                checkInTime: isoIn,
                checkOutTime: isoOut,
                checkInLocation: checkInLocation,
                checkOutLocation: checkOutLocation,
                checkInImage: finalInUrl,
                checkOutImage: finalOutUrl,
                updatedAt: new Date().toISOString()
            }, { merge: true });

            await loadAttendance();
            setOpen(false);
        } catch (e) {
            console.error("Admin attendance sync update failed:", e);
        }
        setLoading(false);
    };

    const calculateMonthlyStats = (activeDate) => {
        const monthStr = format(activeDate, "yyyy-MM");
        let p = 0, a = 0, h = 0, l = 0;
        Object.values(attendanceData).forEach(r => {
            if ((r.staffId === selectedStaff.id || r.uid === selectedStaff.id) && r.date?.startsWith(monthStr)) {
                if (r.status === "Present") p++;
                else if (r.status === "Absent") a++;
                else if (r.status === "Half Day") h++;
                else if (r.status === "Late Mark") l++;
            }
        });
        setCounts({ present: p, absent: a, halfDay: h, late: l });
    };

    // Helper to extract monthly logs for the current view Date
    const currentMonthLogs = Object.values(attendanceData)
        .filter(r => (r.staffId === selectedStaff?.id || r.uid === selectedStaff?.id) && r.date?.startsWith(format(viewDate, "yyyy-MM")))
        .sort((a, b) => b.date.localeCompare(a.date));

    return (
        <Box p={1}>
            <Grid className="row" spacing={3}>
                {/* Left Side Panel */}
                <Grid className="col-sm-4 mb-2" item xs={12} md={3.5}>
                    <Stack direction="row" spacing={1} mb={3} flexWrap="wrap" useFlexGap justifyContent="center">
                        {legendItems.map((item) => (
                            <Stack
                                key={item.label}
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{
                                    px: 1.5, py: 0.5, borderRadius: 10,
                                    bgcolor: item.color, border: `1px solid ${item.border}`
                                }}
                            >
                                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                                    {item.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>

                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Select Staff Member</InputLabel>
                            <Select
                                value={selectedStaff?.id || ""}
                                label="Select Staff Member"
                                onChange={(e) => setSelectedStaff(staffList.find(s => s.id === e.target.value))}
                            >
                                {staffList.map(s => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.name || s.staffName || s.email || s.id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Monthly Attendance Counters */}
                        <Card sx={{ p: 2, bgcolor: '#1a237e', color: 'white', borderRadius: 3, textAlign: 'center' }}>
                            <Typography variant="caption" letterSpacing={1}>
                                {format(viewDate, "MMMM yyyy").toUpperCase()} SUMMARY
                            </Typography>
                            <Stack direction="row" justifyContent="space-around" mt={1.5}>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">{counts.present}</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Present</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">{counts.late}</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Late</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">{counts.halfDay}</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Half Day</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">{counts.absent}</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Absent</Typography>
                                </Box>
                            </Stack>
                        </Card>

                        {/* Monthwise Records List with Location */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, maxHeight: 420, overflow: 'auto' }}>
                            <Typography variant="subtitle2" mb={1.5} fontWeight="bold">
                                {format(viewDate, "MMMM yyyy")} Records ({currentMonthLogs.length})
                            </Typography>
                            {currentMonthLogs.length === 0 ? (
                                <Typography variant="caption" color="textSecondary">No attendance entries for this month.</Typography>
                            ) : (
                                currentMonthLogs.map((log, i) => (
                                    <Box key={i} sx={{ mb: 1.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography variant="caption" fontWeight="bold">
                                                {format(new Date(log.date), "dd MMM yyyy")}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={log.status}
                                                color={
                                                    log.status === "Present" ? "success" :
                                                        log.status === "Late Mark" ? "warning" :
                                                            log.status === "Half Day" ? "info" :
                                                                log.status === "Absent" ? "error" : "default"
                                                }
                                            />
                                        </Stack>

                                        {log.status !== "Absent" && (
                                            <Stack spacing={0.3} mt={1}>
                                                <Typography variant="caption" color="textSecondary" display="block">
                                                    <strong>In:</strong> {log.checkInTime ? format(new Date(log.checkInTime), "hh:mm a") : "--:--"}
                                                    {log.checkInLocation ? ` (${log.checkInLocation})` : log.location ? ` (${log.location})` : ""}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" display="block">
                                                    <strong>Out:</strong> {log.checkOutTime ? format(new Date(log.checkOutTime), "hh:mm a") : "--:--"}
                                                    {log.checkOutLocation ? ` (${log.checkOutLocation})` : ""}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                ))
                            )}
                        </Paper>
                    </Stack>
                </Grid>

                {/* Calendar Main Panel */}
                <Grid className="col-sm-8 mb-2" item xs={12} md={8.5}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                        {selectedStaff ? (
                            <Calendar
                                onClickDay={handleDateClick}
                                tileClassName={({ date, view }) => {
                                    if (view !== 'month') return;
                                    const res = attendanceData[`${selectedStaff.id}_${format(date, "yyyy-MM-dd")}`]?.status;
                                    if (res === "Present") return "present-tile";
                                    if (res === "Absent") return "absent-tile";
                                    if (res === "Half Day") return "halfday-tile";
                                    if (res === "Late Mark") return "late-tile";
                                    if (res === "Holiday") return "holiday-tile";
                                }}
                                onActiveStartDateChange={({ activeStartDate }) => setViewDate(activeStartDate)}
                            />
                        ) : (
                            <Box sx={{ p: 10, textAlign: 'center', color: 'text.secondary' }}>
                                Select a staff member from the left side panel to view their attendance history.
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Edit/Update Attendance Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Attendance: {format(selectedDate || new Date(), "dd MMM yyyy")}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        <Typography variant="subtitle2">Attendance Status</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {["Present", "Absent", "Half Day", "Late Mark"].map(s => (
                                <Button
                                    key={s}
                                    variant={status === s ? "contained" : "outlined"}
                                    onClick={() => setStatus(s)}
                                    size="small"
                                    sx={{
                                        flex: '1 1 45%',
                                        color: s === "Late Mark" && status !== s ? "#ed6c02" : undefined,
                                        borderColor: s === "Late Mark" && status !== s ? "#ed6c02" : undefined,
                                    }}
                                >
                                    {s}
                                </Button>
                            ))}
                        </Stack>

                        {status !== "Absent" && (
                            <>
                                <Divider />
                                <Typography variant="subtitle2">Time & Location Adjustments</Typography>

                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        label="Check In Time"
                                        type="time"
                                        value={checkInTime}
                                        onChange={(e) => setCheckInTime(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 300 }}
                                        fullWidth
                                        size="small"
                                    />
                                    <TextField
                                        label="Check Out Time"
                                        type="time"
                                        value={checkOutTime}
                                        onChange={(e) => setCheckOutTime(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 300 }}
                                        fullWidth
                                        size="small"
                                    />
                                </Stack>

                                <TextField
                                    label="Check In Location"
                                    value={checkInLocation}
                                    onChange={(e) => setCheckInLocation(e.target.value)}
                                    fullWidth
                                    size="small"
                                    placeholder="e.g. Main Office / Branch A"
                                />

                                <TextField
                                    label="Check Out Location"
                                    value={checkOutLocation}
                                    onChange={(e) => setCheckOutLocation(e.target.value)}
                                    fullWidth
                                    size="small"
                                    placeholder="e.g. Main Office"
                                />
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={loading}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Save Record"}
                    </Button>
                </DialogActions>
            </Dialog>

            <style>{`
        .react-calendar { width: 100% !important; border: none !important; }
        .react-calendar__tile { 
          height: 80px !important; 
          border-radius: 8px !important; 
          transition: 0.2s; 
          border: 1px solid transparent !important; 
        }
        .present-tile { background: #e8f5e9 !important; color: #2e7d32 !important; border-color: #a5d6a7 !important; }
        .absent-tile { background: #ffebee !important; color: #d32f2f !important; border-color: #ef9a9a !important; }
        .halfday-tile { background: #e3f2fd !important; color: #1976d2 !important; border-color: #bbdefb !important; font-weight: bold;}
        .late-tile { background: #fff3e0 !important; color: #ed6c02 !important; border-color: #ffcc80 !important; font-weight: bold; }
        .holiday-tile { background: #f5f5f5 !important; color: #9e9e9e !important; border-color: #e0e0e0 !important; opacity: 0.6; }
        .react-calendar__tile:hover { background: #f0f0f0 !important; transform: scale(1.02); z-index: 1; }
      `}</style>
        </Box>
    );
}

export default Staffattendance;